/**
 * The router context (routing-and-state.md §9). Guards read `context.auth.status`
 * and `context.auth.user.role`. `auth` is a LIVE proxy over the Zustand store (via
 * getters) so a guard always sees the current status — not a snapshot captured when
 * the router was built — which is exactly what the ENG-11 slow-refresh case needs.
 */
import type { QueryClient } from '@tanstack/react-query';

import {
  resolveAuthReady,
  useAuth,
  type AuthStatus,
  type AuthUser,
  type SignedInIdentity,
} from '../store/auth-store.ts';

export interface RouterAuthContext {
  /** Identity-level: `authenticated` = an identity session exists (a firm may not be bound yet). */
  readonly status: AuthStatus;
  /** The signed-in identity (worker-auth). */
  readonly identity: SignedInIdentity | null;
  /** The FIRM principal (per-firm role) — non-null only while a firm session is bound. */
  readonly user: AuthUser | null;
  /** ENG-11: await an in-flight refresh when status is still `loading`. */
  ensureResolved: () => Promise<void>;
}

export interface RouterContext {
  queryClient: QueryClient;
  auth: RouterAuthContext;
}

/** A live view of the auth store for the router context. */
export function createRouterAuthContext(): RouterAuthContext {
  return {
    get status(): AuthStatus {
      return useAuth.getState().status;
    },
    get identity(): SignedInIdentity | null {
      return useAuth.getState().identity;
    },
    get user(): AuthUser | null {
      return useAuth.getState().user;
    },
    ensureResolved: resolveAuthReady,
  };
}
